import React, { useState, useEffect } from 'react';
import { getImageUrl } from '../utils/imageUrl';
import config from '../config';
import { Link, useNavigate } from 'react-router-dom';
import Navbar from '../components/Navbar';
import { useAuth } from '../context/AuthContext';
import { Car, Plus, Trash2, Eye, Calendar } from 'lucide-react';
import API from '../services/api';

const MesLocations = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [limiteInfo, setLimiteInfo] = useState(null);

  useEffect(() => {
    if (!user) { navigate('/connexion'); return; }

    API.get('/locations/user/mes-locations').then(async res => {
      setLocations(res.data.locations);

      // Calculer limite globale
      try {
        const annoncesRes = await API.get('/annonces/mes-annonces');
        const actives = annoncesRes.data.annonces.filter(a => a.statut === 'active').length;
        const locationsActives = res.data.locations.filter(l => l.statut !== 'indisponible').length;
        const total = actives + locationsActives;

        const limites = {
          acheteur: 0,
          vendeur_particulier: 10,
          vendeur_pro: null,
          admin: null
        };
        const limite = limites[user.role] !== undefined ? limites[user.role] : null;
        setLimiteInfo({ total, limite });
      } catch (err) {
        console.error(err);
      }
    }).finally(() => setLoading(false));
  }, [user]);

  const handleSupprimer = async (id) => {
    if (!window.confirm('Supprimer cette location ?')) return;
    await API.delete(`/locations/${id}`);
    setLocations(locations.filter(l => l.id !== id));
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar />

      {/* Header */}
      <div style={{ background: '#0A0A0A' }} className="py-8 px-4">
        <div className="max-w-4xl mx-auto flex justify-between items-center">
          <div>
            <h1 className="text-white text-2xl font-black">Mes Locations</h1>
            <p style={{ color: '#F5A623' }} className="text-sm mt-1">{locations.length} véhicule(s)</p>
          </div>
          <Link
            to="/creer-location"
            style={{ background: '#F5A623' }}
            className="text-black px-4 py-2 rounded-xl flex items-center gap-2 font-bold hover:opacity-90 transition"
          >
            <Plus size={16} /> Ajouter
          </Link>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 py-8">

        {/* Limite globale */}
        {limiteInfo && limiteInfo.limite !== null && (
          <div style={{ background: '#fffbeb', border: '1px solid #F5A623' }} className="rounded-2xl p-4 mb-6">
            <div className="flex items-center justify-between mb-2">
              <p className="text-sm font-bold text-gray-700">
                📊 Annonces actives (vente + location) : {limiteInfo.total} / {limiteInfo.limite}
              </p>
              <span style={{ color: '#F5A623' }} className="text-xs font-bold">Plan gratuit</span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-2 mb-3">
              <div
                style={{
                  width: `${Math.min((limiteInfo.total / limiteInfo.limite) * 100, 100)}%`,
                  background: limiteInfo.total >= limiteInfo.limite ? '#ef4444' : '#F5A623'
                }}
                className="h-2 rounded-full transition-all"
              />
            </div>
            {limiteInfo.total >= limiteInfo.limite ? (
              <div className="flex items-center justify-between">
                <p className="text-xs text-red-500 font-semibold">
                  ⚠️ Limite atteinte — Passez à un plan pro pour publier plus
                </p>
                <Link
                  to="/abonnements"
                  style={{ background: '#F5A623' }}
                  className="text-black text-xs font-black px-3 py-1 rounded-full hover:opacity-90 transition"
                >
                  Voir les plans
                </Link>
              </div>
            ) : (
              <p className="text-xs text-gray-500">
                Il vous reste <strong>{limiteInfo.limite - limiteInfo.total}</strong> annonce(s) disponible(s)
              </p>
            )}
          </div>
        )}

        {loading ? (
          <div className="text-center py-12 text-gray-400">Chargement...</div>
        ) : locations.length === 0 ? (
          <div className="text-center py-12 bg-white rounded-2xl border border-gray-100">
            <Car size={60} className="mx-auto mb-4 text-gray-200" />
            <p className="text-gray-400 font-semibold mb-4">Aucun véhicule à la location</p>
            <Link
              to="/creer-location"
              style={{ background: '#F5A623' }}
              className="text-black px-6 py-3 rounded-xl font-bold hover:opacity-90 transition"
            >
              Proposer un véhicule
            </Link>
          </div>
        ) : (
          <div className="space-y-4">
            {locations.map(location => (
              <div key={location.id} className="bg-white rounded-2xl shadow-sm p-4 border border-gray-100">
                <div className="flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-4">

                  <div className="flex items-start gap-3 sm:flex-1 min-w-0">
                    {/* Photo */}
                    <div className="w-20 h-20 sm:w-24 sm:h-20 bg-gray-100 rounded-xl flex items-center justify-center flex-shrink-0 overflow-hidden">
                      {location.photo_principale ? (
                        <img
                          src={getImageUrl(location.photo_principale)}
                          alt={location.titre}
                          className="w-full h-full object-cover rounded-xl"
                        />
                      ) : (
                        <Car size={32} className="text-gray-300" />
                      )}
                    </div>

                    {/* Infos */}
                    <div className="flex-1 min-w-0">
                      <h3 className="font-medium text-gray-900 line-clamp-2 leading-snug">{location.titre}</h3>
                      <p className="text-sm font-semibold text-gray-900 mt-0.5">
                        {parseInt(location.prix_jour).toLocaleString('fr-FR')} FCFA/jour
                      </p>
                      <div className="flex gap-2 mt-1.5 flex-wrap">
                        <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${
                          location.statut === 'disponible' ? 'bg-green-100 text-green-600' :
                          location.statut === 'loue' ? 'bg-blue-100 text-blue-600' :
                          'bg-gray-100 text-gray-500'
                        }`}>
                          {location.statut}
                        </span>
                        {location.reservations_attente > 0 && (
                          <span className="px-2 py-0.5 rounded-full text-xs font-semibold bg-yellow-100 text-yellow-600">
                            {location.reservations_attente} demande(s)
                          </span>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Actions */}
                  <div className="flex gap-1 justify-end sm:flex-shrink-0">
                    <Link
                      to={`/locations/${location.id}`}
                      className="p-2 text-blue-400 hover:bg-blue-50 rounded-xl transition"
                      title="Voir"
                    >
                      <Eye size={18} />
                    </Link>
                    <Link
                      to="/mes-reservations"
                      className="p-2 text-yellow-500 hover:bg-yellow-50 rounded-xl transition"
                      title="Réservations"
                    >
                      <Calendar size={18} />
                    </Link>
                    <button
                      onClick={() => handleSupprimer(location.id)}
                      className="p-2 text-red-400 hover:bg-red-50 rounded-xl transition"
                      title="Supprimer"
                    >
                      <Trash2 size={18} />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default MesLocations;